import NiceModal from '@ebay/nice-modal-react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'

import { ComponentErrorBoundary } from '@/components/atomic/ComponentErrorBoundary'
import { PageContainer } from '@/components/atomic/PageContainer'
import { PageMetaData } from '@/components/atomic/PageMetaData'
import { PartnerLinkGroup } from '@/components/styleguide/Configurator/PartnerLinkGroup'
import { StickyButtons } from '@/components/styleguide/Configurator/StickyButtons'
import { VehicleImage } from '@/components/styleguide/Configurator/VehicleImage'
import { VehicleView } from '@/components/styleguide/Configurator/VehicleView'
import { Header } from '@/components/styleguide/Header'
import { ShareAndSave } from '@/components/views/ConfigPage/ToolbarMobile/ShareAndSave'
import { ProfileEditor } from '@/components/views/ProfileEditor'
import {
  constants,
  FILTER_LEASE_TYPES_PERSONAL,
  FILTER_VEHICLE_TYPES_CARS,
  STATUS_PRE_REGISTER,
} from '@/lib/constants'
import { useExperiment } from '@/lib/hooks/useExperiment'
import { getPcpPricesForPersonalService } from '@/lib/services/searchService'
import {
  getConfiguratorResultsService,
  getVehiclePaintService,
  getVehicleReviewService,
  getVehicleService,
  getVehicleStandardOptionsService,
  getVehicleTechnicalDataService,
} from '@/lib/services/vehicleService'
import { addVatToPrice, calculateTotalDealCost } from '@/lib/utilities/search'
import { createUnixTimestampForCurrentTime, storageAvailable } from '@/lib/utilities/system'
import {
  addToDataLayer,
  generateEventPayloadForVehiclePrice,
  getUrlReadyString,
} from '@/lib/utilities/vehicle'
import { postAnalyticsEvent } from '@/store/actions/event'
import { storeActiveLeaseProfile } from '@/store/reducers/leaseProfile'
import { storeShowBenefitsModal } from '@/store/reducers/system'
import {
  clearPaintOptionVehicle,
  clearSelectedPaintOption,
  storeExteriorImages,
  storeInteriorImages,
  storePaintOptions,
} from '@/store/reducers/vehicle'
import { selectLeaseProfile } from '@/store/selectors/leaseProfile'
import { selectIsFirstRender } from '@/store/selectors/system'
import { selectUsersVisitorKey } from '@/store/selectors/user'
import { selectPaintOptions, selectSelectedVehiclePaintOption } from '@/store/selectors/vehicle'
import { Theme } from '@/types/system'

const ColourOptionsSelector = dynamic(() => import('@/components/views/ConfigPage/ColourOptions'))
const Title = dynamic(() => import('@/components/styleguide/Configurator/Title'))
const VehicleDataSpotlightGroup = dynamic(
  () => import('@/components/styleguide/Configurator/VehicleDataSpotlight'),
)
const Gallery = dynamic(() => import('@/components/views/ConfigPage/Gallery'))
const Toolbar = dynamic(() => import('@/components/styleguide/Configurator/Toolbar'))
const PriceHistory = dynamic(() => import('@/components/views/ConfigPage/PriceHistory'))
const Review = dynamic(() => import('@/components/views/ConfigPage/Review'))
const Specification = dynamic(() => import('@/components/views/ConfigPage/Specification'))
const PartnerLink = dynamic(() => import('@/components/styleguide/Configurator/PartnerLink'))
const Configurator = dynamic(() => import('@/components/views/ConfigPage/Configurator'), {
  ssr: false,
})
const DealsUnavailable = dynamic(
  () => import('@/components/styleguide/Configurator/DealsUnavailable'),
  {
    ssr: false,
  },
)
const PartnerHeaderLinks = dynamic(
  () => import('@/components/styleguide/Configurator/PartnerHeaderLinks'),
  {
    ssr: false,
  },
)
const AuthenticationModal = dynamic(() => import('@/components/modals/AuthenticationModal'), {
  ssr: false,
})
const Interior360Image = dynamic(() => import('@/components/styleguide/Interior360Image'), {
  ssr: false,
})

const CarConfigPage = ({
  originalVehicle,
  originalLeaseProfile,
  originalReview,
  originalStandardOptions,
  originalTechnicalData,
  vehiclePriceId,
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [experimentGroup] = useExperiment()
  const isDesktop = useMediaQuery({ minWidth: 992 })

  const visitorKey = useSelector(selectUsersVisitorKey)
  const isFirstRender = useSelector(selectIsFirstRender)
  const leaseProfile = useSelector(selectLeaseProfile)
  const paintOptions = useSelector(selectPaintOptions)
  const selectedPaintOption = useSelector(selectSelectedVehiclePaintOption)

  const [insuranceRef, setInsuranceRef] = useState(null)
  const [vehicle, setVehicle] = useState(originalVehicle)
  const [selectedVehiclePrice, setSelectedVehiclePrice] = useState(null)
  const [technicalData, setTechnicalData] = useState(originalTechnicalData)
  const [standardOptions, setStandardOptions] = useState(originalStandardOptions)
  const [review, setReview] = useState(originalReview)
  const [vehiclePriceTagDictionary, setVehiclePriceTagDictionary] = useState([])
  const [showAlert, setShowAlert] = useState(false)
  const [componentStatus, setComponentStatus] = useState(constants.componentStatus.LOADING)
  const [activeSection, setActiveSection] = useState(constants.configPageSections.OVERVIEW)
  const [pcpDifference, setPcpDifference] = useState(null)
  const [configMode, setConfigMode] = useState(constants.vehicleConfigModes.BEST_VALUE)
  const [hasConfigModeChanged, setHasConfigModeChanged] = useState(false)
  const [hasLeaseProfileChanged, setHasLeaseProfileChanged] = useState(false)
  const [openSelector, setOpenSelector] = useState('')
  const [imageStatus, setImageStatus] = useState(constants.imageStatus.EXTERIOR)

  useEffect(() => {
    if (typeof window.ttq !== 'undefined' && selectedVehiclePrice) {
      window.ttq.track('ViewContent', {
        /* eslint-disable */
        content_id: `${selectedVehiclePrice.id}`,
        content_type: 'product',
        content_name: `${vehicle.manufacturerName} ${vehicle.modelName} ${vehicle.derivativeName}`,
        /* eslint-enable */
        quantity: 1,
        value:
          selectedVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
            ? addVatToPrice(calculateTotalDealCost(selectedVehiclePrice))
            : calculateTotalDealCost(selectedVehiclePrice),
        price: `${
          selectedVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
            ? addVatToPrice(selectedVehiclePrice.monthlyPayment)
            : selectedVehiclePrice.monthlyPayment.toFixed(2)
        }`,
        currency: 'GBP',
      })
    }
  }, [selectedVehiclePrice, vehicle.manufacturerName, vehicle.modelName, vehicle.derivativeName])

  useEffect(() => {
    // If it's the first render on this page then no benefits modal is needed
    if (isFirstRender) {
      dispatch(storeShowBenefitsModal(false))
    }
  }, [dispatch, isFirstRender])

  useEffect(() => {
    dispatch(storeActiveLeaseProfile(originalLeaseProfile))
  }, [dispatch, originalLeaseProfile])

  useEffect(() => {
    const doesPaintExist = paintOptions.filter(option => selectedPaintOption?.id === option.id)

    if (doesPaintExist.length > 0) {
      dispatch(storeExteriorImages(selectedPaintOption.images))
    } else {
      dispatch(clearSelectedPaintOption())
      dispatch(clearPaintOptionVehicle())
      dispatch(
        storeExteriorImages(
          originalVehicle.gallery?.images.filter(image => image.angleType === 'EXTERIOR') || [],
        ),
      )
      dispatch(
        storeInteriorImages(
          originalVehicle.gallery?.images.filter(image => image.angleType === 'INTERIOR') || [],
        ),
      )
    }
  }, [dispatch, originalVehicle, selectedPaintOption, paintOptions, vehicle])

  useEffect(() => {
    // This sets up a timer of 5 seconds before hiding the alert
    if (showAlert) {
      let alertTimer = setTimeout(() => setShowAlert(false), 5000)

      return () => {
        clearTimeout(alertTimer)
      }
    }
  }, [showAlert])

  useEffect(() => {
    // This gets the initial prices for the vehicle
    const payload = {
      vehicleType: FILTER_VEHICLE_TYPES_CARS,
      mileage: originalLeaseProfile.mileage,
      term: originalLeaseProfile.term,
      leaseType: originalLeaseProfile.leaseType,
      initialPaymentInMonths: originalLeaseProfile.initialPaymentInMonths,
      stockStatus: originalLeaseProfile.stockStatus,
      range: originalVehicle.range,
      model: originalVehicle.model,
      grouping: 0,
      sorting: constants.vehicleConfigModes.BEST_VALUE,
    }

    setComponentStatus(constants.componentStatus.LOADING)
    const getVehiclePrice = async params => {
      const response = await getConfiguratorResultsService(params)

      return response
    }

    const storeRecentlyViewed = vehiclePrice => {
      const viewedVehicle = {
        vehicle: originalVehicle,
        vehiclePrice,
      }

      if (storageAvailable('localStorage')) {
        let recentlyViewed =
          JSON.parse(localStorage.getItem(constants.localStorageKey.recentlyViewedCars)) || []
        const existingIndex = recentlyViewed.findIndex(
          item => item.vehicle.id === originalVehicle.id,
        )

        if (existingIndex !== -1) {
          recentlyViewed.splice(existingIndex, 1)
        }

        recentlyViewed.push(viewedVehicle)

        if (recentlyViewed.length > 24) {
          recentlyViewed.shift()
        }

        localStorage.setItem(
          constants.localStorageKey.recentlyViewedCars,
          JSON.stringify(recentlyViewed),
        )
      }
    }

    const selectVehiclePrice = results => {
      const selectedDeal = results.filter(item => item.vehiclePrice.id === vehiclePriceId)
      const selectedVehicle = results.filter(
        item => item.vehiclePrice.vehicleId === originalLeaseProfile.vehicleId,
      )
      const vehiclePrice =
        selectedDeal.length > 0
          ? selectedDeal[0].vehiclePrice
          : selectedVehicle.length > 0
            ? selectedVehicle[0].vehiclePrice
            : results[0].vehiclePrice
      const eventData = generateEventPayloadForVehiclePrice(vehiclePrice)

      if (!!vehiclePrice && !!originalVehicle) {
        storeRecentlyViewed(vehiclePrice)
      }

      setSelectedVehiclePrice(vehiclePrice)
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.vehicleConfigRequest,
          payload: eventData,
        }),
      )
      setVehiclePriceTagDictionary(results.flatMap(item => item.vehiclePriceTags))
    }

    getVehiclePrice({
      vehicleId: originalLeaseProfile.vehicleId,
      ...payload,
    })
      .then(res => {
        if (res.results.length > 0) {
          selectVehiclePrice(res.results)
        } else {
          getVehiclePrice(payload).then(backup => {
            if (backup.results.length > 0) {
              selectVehiclePrice(backup.results)
            }
          })
        }
      })
      .finally(() => {
        setComponentStatus(constants.componentStatus.COMPLETE)
      })
  }, [originalLeaseProfile, originalVehicle, vehiclePriceId, dispatch])

  useEffect(() => {
    // This gets the prices for the vehicle when config mode is changed

    if (hasConfigModeChanged) {
      setComponentStatus(constants.componentStatus.LOADING)
      const getVehiclePrice = async params => {
        const response = await getConfiguratorResultsService(params)

        return response
      }

      getVehiclePrice({
        vehicleType: FILTER_VEHICLE_TYPES_CARS,
        mileage: leaseProfile.mileage,
        term: leaseProfile.term,
        leaseType: leaseProfile.leaseType,
        initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
        stockStatus: leaseProfile.stockStatus,
        range: originalVehicle.range,
        model: originalVehicle.model,
        grouping: 0,
        sorting: configMode,
      })
        .then(res => {
          if (res.results.length > 0) {
            const vehiclePrice = res.results[0].vehiclePrice

            setSelectedVehiclePrice(vehiclePrice)
            setVehiclePriceTagDictionary(res.results.flatMap(item => item.vehiclePriceTags))

            if (vehiclePrice.vehicleId !== leaseProfile.vehicleId) {
              dispatch(
                storeActiveLeaseProfile({
                  mileage: vehiclePrice.mileage,
                  term: vehiclePrice.term,
                  leaseType: vehiclePrice.leaseType,
                  initialPaymentInMonths: vehiclePrice.initialPaymentInMonths,
                  stockStatus: vehiclePrice.stockStatus,
                  vehicleId: vehiclePrice.vehicleId,
                }),
              )
            }
          }
        })
        .finally(() => {
          setHasConfigModeChanged(false)
          setComponentStatus(constants.componentStatus.COMPLETE)
        })
    }
  }, [configMode, leaseProfile, originalVehicle, hasConfigModeChanged, dispatch])

  useEffect(() => {
    // This gets the prices for the vehicle when lease profile mode has changed

    if (hasLeaseProfileChanged) {
      setComponentStatus(constants.componentStatus.LOADING)
      const getVehiclePrice = async params => {
        const response = await getConfiguratorResultsService(params)

        return response
      }

      getVehiclePrice({
        vehicleType: FILTER_VEHICLE_TYPES_CARS,
        mileage: leaseProfile.mileage,
        term: leaseProfile.term,
        leaseType: leaseProfile.leaseType,
        initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
        stockStatus: leaseProfile.stockStatus,
        range: vehicle.range,
        model: vehicle.model,
        vehicleId: leaseProfile.vehicleId,
        grouping: 0,
        sorting: configMode,
      })
        .then(res => {
          if (res.results.length > 0) {
            const vehiclePrice = res.results[0].vehiclePrice

            setSelectedVehiclePrice(vehiclePrice)
            setVehiclePriceTagDictionary(res.results.flatMap(item => item.vehiclePriceTags))
          }
        })
        .finally(() => {
          setHasLeaseProfileChanged(false)
          setComponentStatus(constants.componentStatus.COMPLETE)
        })
    }
  }, [configMode, leaseProfile, vehicle, hasLeaseProfileChanged, dispatch])

  useEffect(() => {
    // This updates the vehicle object if the selectedVehiclePrice vehicleId differs

    if (!!selectedVehiclePrice && selectedVehiclePrice.vehicleId !== vehicle.id) {
      const getVehicle = async params => {
        const response = await getVehicleService(params)

        return response
      }

      getVehicle({
        id: selectedVehiclePrice.vehicleId,
      }).then(res => {
        setVehicle(res)
        dispatch(storeExteriorImages(res.gallery?.images || []))
      })
    }
  }, [selectedVehiclePrice, vehicle.id, dispatch])

  useEffect(() => {
    // This fetches technical data if vehicle id changes
    const getNewVehicleData = async params => {
      const standardOptions = await getVehicleStandardOptionsService({
        id: params.id,
        vehicleType: FILTER_VEHICLE_TYPES_CARS,
      })

      const technicalData = await getVehicleTechnicalDataService({
        id: params.id,
      })

      return { technicalData, standardOptions }
    }

    getNewVehicleData({
      id: vehicle.id,
    }).then(res => {
      setTechnicalData(res.technicalData)
      setStandardOptions(res.standardOptions)
    })
  }, [vehicle.id])

  useEffect(() => {
    // This fetches paint data if vehicle id changes
    const getNewVehiclePaintData = async vehicleId => {
      const paintOptions = await getVehiclePaintService({
        vehicleId,
      })

      return paintOptions
    }

    getNewVehiclePaintData(vehicle.id).then(res => {
      let filteredResults = res.filter(item => item.id !== null)
      dispatch(storePaintOptions(filteredResults))
    })
  }, [vehicle.id, dispatch])

  useEffect(() => {
    if (selectedVehiclePrice) {
      getPcpPricesForPersonalService({
        term: selectedVehiclePrice.term,
        mileage: selectedVehiclePrice.mileage,
        initialPaymentInMonths: selectedVehiclePrice.initialPaymentInMonths,
        vehicleId: vehicle.id,
      })
        .then(res => {
          let difference =
            addVatToPrice(res.monthlyPayment) - addVatToPrice(selectedVehiclePrice.monthlyPayment)
          setPcpDifference(difference)
        })
        .catch(() => {
          setPcpDifference(null)
        })
    }
  }, [vehicle.id, selectedVehiclePrice])

  useEffect(() => {
    // This fetches review data if vehicle id changes
    const getVehicleReviewData = async params => {
      const review = await getVehicleReviewService({
        id: params.id,
      })

      return review
    }

    getVehicleReviewData({
      id: vehicle.id,
    })
      .then(res => {
        setReview(res)
      })
      .catch(() => {
        setReview(null)
      })
  }, [vehicle.id])

  const onOtherButtonClick = (buttonPosition, type) => {
    addToDataLayer({
      event: type.toLowerCase().split('_').join('-'),
      action: 'button-click',
      position: buttonPosition,
      experimentGroup,
    })

    dispatch(
      postAnalyticsEvent({
        event: type.toUpperCase().split('-').join('_'),
        payload: {
          position: buttonPosition,
        },
      }),
    )
  }

  useEffect(() => {
    if (visitorKey) {
      const ref = Buffer.from(
        JSON.stringify({
          vp: selectedVehiclePrice?.id,
          vi: vehicle.id,
          vk: visitorKey.substr(visitorKey.length - 7),
          ts: createUnixTimestampForCurrentTime(),
        }),
      ).toString('base64')
      setInsuranceRef(ref)
    }
  }, [visitorKey, selectedVehiclePrice, vehicle, technicalData])

  useEffect(() => {
    if (activeSection !== constants.configPageSections.OVERVIEW) {
      setImageStatus(constants.imageStatus.EXTERIOR)
    }
  }, [activeSection])

  const onLoginButtonClick = () => {
    NiceModal.show(AuthenticationModal, {
      initialStatus: STATUS_PRE_REGISTER,
    })
  }

  return (
    <>
      <PageMetaData
        vehicle={vehicle}
        leaseProfile={leaseProfile}
        title={`${vehicle.manufacturerName} ${vehicle.modelName} ${vehicle.derivativeName} Lease: ${originalLeaseProfile.term} Month ${originalLeaseProfile.mileage} Miles ${originalLeaseProfile.initialPaymentInMonths} Months Upfront`}
        description={`Lease a ${vehicle.manufacturerName} ${vehicle.modelName} ${vehicle.derivativeName} Over ${originalLeaseProfile.term} Months with ${leaseProfile.mileage} Miles PA and ${originalLeaseProfile.initialPaymentInMonths} Months Upfront Today Via LeaseLoco`}
        pageUrl={router.asPath}
        canonical={`${process.env.NEXT_PUBLIC_URL}/car-leasing/${getUrlReadyString(
          vehicle.manufacturerName,
        )}/${getUrlReadyString(vehicle.rangeName)}`}
        noIndex
      />
      <Header
        backButtonUrl="/car-leasing/search"
        hideNav
        navDesktopCenter={
          leaseProfile.vehicleId && (
            <ProfileEditor
              leaseProfile={leaseProfile}
              vehicleType={FILTER_VEHICLE_TYPES_CARS}
              setHasLeaseProfileChanged={setHasLeaseProfileChanged}
              openSelectorOverride={openSelector}
              setOpenSelectorOverride={arg => setOpenSelector(arg)}
            />
          )
        }
        navMobileRight={
          selectedVehiclePrice && (
            <ShareAndSave
              selectedVehiclePrice={selectedVehiclePrice}
              onLoginButtonClick={onLoginButtonClick}
            />
          )
        }
        isLogoSticky={
          activeSection === constants.configPageSections.OVERVIEW ||
          activeSection === constants.configPageSections.COLOUR_OPTIONS
        }
        theme={Theme.light}
        backgroundColor="backdrop"
      />
      <section
        className={`container--config-image${imageStatus.includes(constants.imageStatus.INTERIOR) ? ' no-padding' : ''}`}>
        {imageStatus.includes(constants.imageStatus.EXTERIOR) && (
          <VehicleImage
            vehicle={vehicle}
            setActiveSection={setActiveSection}
            activeSection={activeSection}
          />
        )}
        {imageStatus.includes(constants.imageStatus.INTERIOR) && (
          <Interior360Image
            path={vehicle.gallery.interior360Path}
            imageStatus={imageStatus}
          />
        )}
        {(activeSection === constants.configPageSections.OVERVIEW ||
          activeSection === constants.configPageSections.COLOUR_OPTIONS) &&
          vehicle?.gallery?.interior360Path && (
            <StickyButtons
              imageStatus={imageStatus}
              setImageStatus={setImageStatus}
              openSelector={openSelector}
              setActiveSection={setActiveSection}
              activeSection={activeSection}
              vehicle={vehicle}
            />
          )}
      </section>
      <div className="container-fluid container--grey container--config-page">
        {!isDesktop && (
          <div className="config-page__title">
            <Title
              vehicle={vehicle}
              selectedVehiclePrice={selectedVehiclePrice}
            />
            <div className="config-page__spotlight">
              <VehicleDataSpotlightGroup
                technicalData={technicalData}
                vehicleFuelType={vehicle.fuelType}
              />
            </div>
          </div>
        )}
        <PageContainer containerClass="container--config">
          <section className="config-page__vehicle-config">
            {isDesktop && (
              <div
                className={`config-page__content${imageStatus.includes(constants.imageStatus.INTERIOR) ? ' interior-view' : ''}`}>
                <Title
                  vehicle={vehicle}
                  selectedVehiclePrice={selectedVehiclePrice}
                  isSticky={
                    activeSection === constants.configPageSections.OVERVIEW ||
                    activeSection === constants.configPageSections.COLOUR_OPTIONS
                  }
                />
                {(activeSection === constants.configPageSections.OVERVIEW ||
                  activeSection === constants.configPageSections.COLOUR_OPTIONS ||
                  activeSection === constants.configPageSections.SPECIFICATION) &&
                  imageStatus.includes(constants.imageStatus.EXTERIOR) && (
                    <div
                      className={`config-page__vehicle-view ${activeSection !== constants.configPageSections.SPECIFICATION ? 'config-page__vehicle-view--sticky' : ''}`}>
                      <VehicleView
                        vehicle={vehicle}
                        technicalData={technicalData}
                        setActiveSection={setActiveSection}
                        activeSection={activeSection}
                      />
                      {vehicle?.gallery?.interior360Path && (
                        <StickyButtons
                          imageStatus={imageStatus}
                          setImageStatus={setImageStatus}
                          openSelector={openSelector}
                          setActiveSection={setActiveSection}
                          activeSection={activeSection}
                          vehicle={vehicle}
                        />
                      )}
                    </div>
                  )}
                {activeSection === constants.configPageSections.OVERVIEW &&
                  imageStatus.includes(constants.imageStatus.INTERIOR) && (
                    <div className="config-page__vehicle-view config-page__vehicle-view--sticky">
                      <Interior360Image
                        path={vehicle.gallery.interior360Path}
                        imageStatus={imageStatus}
                      />
                      <StickyButtons
                        imageStatus={imageStatus}
                        setImageStatus={setImageStatus}
                        openSelector={openSelector}
                        setActiveSection={setActiveSection}
                        activeSection={activeSection}
                        vehicle={vehicle}
                      />
                    </div>
                  )}
                {activeSection === constants.configPageSections.SPECIFICATION && (
                  <Specification
                    vehicle={vehicle}
                    technicalData={technicalData}
                    standardOptions={standardOptions}
                  />
                )}
                {activeSection === constants.configPageSections.PRICE_HISTORY && (
                  <PriceHistory
                    selectedVehiclePrice={selectedVehiclePrice}
                    pcpDifference={pcpDifference}
                  />
                )}
                {activeSection === constants.configPageSections.GALLERY && (
                  <Gallery
                    vehicle={vehicle}
                    setActiveSection={setActiveSection}
                  />
                )}
                {activeSection === constants.configPageSections.REVIEW && (
                  <Review
                    review={review}
                    vehicle={vehicle}
                  />
                )}
              </div>
            )}
            <div className="config-page__config">
              {!selectedVehiclePrice && componentStatus !== constants.componentStatus.LOADING && (
                <DealsUnavailable vehicle={vehicle} />
              )}
              {!!selectedVehiclePrice && (
                <ComponentErrorBoundary>
                  <PartnerHeaderLinks
                    onOtherButtonClick={onOtherButtonClick}
                    insuranceRef={insuranceRef}
                    vehicleType={selectedVehiclePrice.vehicleType}
                  />
                  {activeSection !== constants.configPageSections.COLOUR_OPTIONS && (
                    <Configurator
                      setSelectedVehiclePrice={setSelectedVehiclePrice}
                      selectedVehiclePrice={selectedVehiclePrice}
                      standardOptions={standardOptions}
                      technicalData={technicalData}
                      setShowAlert={setShowAlert}
                      vehiclePriceTagDictionary={vehiclePriceTagDictionary}
                      setVehiclePriceTagDictionary={setVehiclePriceTagDictionary}
                      configMode={configMode}
                      setConfigMode={setConfigMode}
                      setHasConfigModeChanged={setHasConfigModeChanged}
                      pcpDifference={pcpDifference}
                      setOpenSelector={setOpenSelector}
                      setActiveSection={setActiveSection}
                    />
                  )}
                  {activeSection === constants.configPageSections.COLOUR_OPTIONS && (
                    <ColourOptionsSelector
                      vehicle={vehicle}
                      setActiveSection={setActiveSection}
                    />
                  )}
                  <PartnerLinkGroup>
                    <PartnerLink
                      icon={`${constants.imageHost.assets}/branding/msm-circle.svg`}
                      heading="Compare car insurance quotes"
                      body="With deals from over 170 providers, MoneySuperMarket helps you compare car insurance prices to find the policy that is right for you."
                      link={`${constants.insuranceUrl.dealPage}?utm_source=leaseloco&utm_medium=affiliate&utm_campaign=config-middle&ref1=config-middle`}
                      linkText="Get a quote"
                      type={constants.promotionalType.INSURANCE}
                      onClick={onOtherButtonClick}
                    />
                    <PartnerLink
                      icon={`${constants.imageHost.assets}/branding/motorway-icon-2022.svg`}
                      heading={`Need to value your car?`}
                      body={`5,000+ dealers compete to buy your car. 84% of customers get more money with Motorway.<sup>1</sup>`}
                      link={`${constants.sellYourVehicleUrl.dealPage}/partner/leaseloco?utm_source=leaseloco&utm_medium=affiliate&utm_campaign=config-middle`}
                      linkText={`Value your car`}
                      type={constants.promotionalType.SELL_YOUR_VEHICLE}
                      onClick={onOtherButtonClick}
                    />
                  </PartnerLinkGroup>
                </ComponentErrorBoundary>
              )}
            </div>
          </section>
        </PageContainer>
      </div>
      {selectedVehiclePrice !== null && (
        <Toolbar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          selectedVehiclePrice={selectedVehiclePrice}
          theme={activeSection === constants.configPageSections.GALLERY ? Theme.dark : Theme.light}
          vehicle={vehicle}
          technicalData={technicalData}
          standardOptions={standardOptions}
          pcpDifference={pcpDifference}
          review={review}
          setHasLeaseProfileChanged={setHasLeaseProfileChanged}
          setOpenSelector={setOpenSelector}
          openSelector={openSelector}
          imageStatus={imageStatus}
        />
      )}
    </>
  )
}

export default CarConfigPage

export const getServerSideProps = async context => {
  const vehicleId = context.params.vehicleId
  const leaseProfile = context.params.leaseProfile
  const vehiclePriceId = context.params.vehiclePriceId
  let standardOptions
  let review
  let technicalData
  let vehicle

  const leaseProfileObject = {
    vehicleId: Number(vehicleId),
    leaseType: Number(leaseProfile.split('-')[0]),
    term: Number(leaseProfile.split('-')[1]),
    mileage: Number(leaseProfile.split('-')[2]),
    initialPaymentInMonths: Number(leaseProfile.split('-')[3]),
    stockStatus: Number(leaseProfile.split('-')[4]),
  }

  try {
    try {
      const response = await getVehicleService({
        id: vehicleId,
      })

      vehicle = response
    } catch (e) {
      vehicle = null
    }

    try {
      const response = await getVehicleTechnicalDataService({
        id: vehicleId,
      })

      technicalData = response
    } catch (e) {
      technicalData = null
    }

    try {
      const response = await getVehicleReviewService({
        id: vehicleId,
      })

      review = response
    } catch (e) {
      review = null
    }

    try {
      const response = await getVehicleStandardOptionsService({
        id: vehicleId,
        vehicleType: FILTER_VEHICLE_TYPES_CARS,
      })

      standardOptions = response
    } catch (e) {
      standardOptions = null
    }

    return {
      props: {
        originalVehicle: vehicle,
        originalLeaseProfile: leaseProfileObject,
        originalReview: review,
        originalStandardOptions: standardOptions,
        originalTechnicalData: technicalData,
        vehiclePriceId,
      },
    }
  } catch (e) {
    return {
      props: {
        error: e,
      },
    }
  }
}
